import {getTestProps} from '@benchling/js-test-utils';
import {Modal, Button, Group, Text, Stack, Radio, createStyles} from '@mantine/core';
import classNames from 'classnames';
import React, {useCallback, useEffect, useState} from 'react';

import {ZINDEX_FLOATING_UI} from 'styles/StyleConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Chip, {ChipSize} from 'ui-library/chips/Chip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import ModalFooter from 'ui-library/modals/ModalFooter';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

export interface TransitionOption {
  transitionId: string;
  fromNodes: Array<{name: string; icon?: BenchlingIconName}>;
  toNode: {name: string; icon?: BenchlingIconName};
}

export interface MergeTransitionModalResult {
  shouldMerge: boolean;
  selectedTransitionId?: string;
}

export interface MergeTransitionModalProps {
  onComplete: (result: MergeTransitionModalResult) => void;
  onClose: () => void;
  opened: boolean;
  existingTransitions: Array<TransitionOption>;
  newSourceNodeName: string;
  targetNodeName: string;
}

enum ModalStep {
  CHOOSE_ACTION = 'choose-action',
  SELECT_TRANSITION = 'select-transition',
}

const useStyles = createStyles((theme) => ({
  chipList: {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: theme.spacing.xs,
  },
  displayOnlyChip: {
    '&.chip, &.chip:hover, &.chip:focus, &.chip:focus-visible, &.chip:active': {
      backgroundColor: theme.colors.gray[2],
      borderColor: theme.colors.gray[2],
      outline: 'none',
    },
  },
  fromColumn: {
    '&&': {
      width: 'calc(50% - 44px)',
    },
  },
  toColumn: {
    '&&': {
      width: 'calc(50% - 44px)',
    },
  },
  radioColumn: {
    '&&': {
      width: '48px',
      paddingLeft: theme.spacing.sm,
      paddingRight: theme.spacing.xs,
    },
  },
  wrapCellText: {
    wordBreak: 'break-word' as const,
    overflowWrap: 'break-word' as const,
    whiteSpace: 'normal' as const,
  },
  tableWrapper: {
    overflow: 'hidden',
  },
  selectedRow: {
    backgroundColor: theme.colors.blue[0],
  },
  clickableRow: {
    cursor: 'pointer',
  },
}));

function ChooseActionStep({
  targetNodeName,
  newSourceNodeName,
  existingTransitionCount,
  onKeepSeparate,
  onMerge,
}: {
  targetNodeName: string;
  newSourceNodeName: string;
  existingTransitionCount: number;
  onKeepSeparate: () => void;
  onMerge: () => void;
}): JSX.Element {
  return (
    <>
      <Stack spacing="sm">
        <Text>
          <strong>{targetNodeName}</strong> already has{' '}
          {existingTransitionCount === 1 ? '1 transition' : `${existingTransitionCount} transitions`}. Merge{' '}
          <strong>{newSourceNodeName}</strong> into one of them, or keep it as its own transition?
        </Text>
        <InlineBanner
          bannerType={InlineBannerType.INFO}
          supportingInfo="During execution, merged transitions wait for all source nodes to complete before the downstream task is created and mapped. Separate transitions run independently."
        />
      </Stack>
      <ModalFooter>
        <Group position="right">
          <Button
            variant="tertiary"
            onClick={onKeepSeparate}
            {...getTestProps({component: 'MergeTransitionModal', element: 'keepSeparateButton'})}
          >
            Keep separate
          </Button>
          <Button
            onClick={onMerge}
            data-autofocus={true}
            {...getTestProps({component: 'MergeTransitionModal', element: 'mergeButton'})}
          >
            Merge
          </Button>
        </Group>
      </ModalFooter>
    </>
  );
}

function SelectTransitionStep({
  newSourceNodeName,
  existingTransitions,
  selectedTransitionId,
  onSelectTransition,
  onBack,
  onConfirm,
}: {
  newSourceNodeName: string;
  existingTransitions: Array<TransitionOption>;
  selectedTransitionId: string | null;
  onSelectTransition: (transitionId: string) => void;
  onBack: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const {classes} = useStyles();

  const handleRowClick = useCallback(
    (rowIndex: number) => {
      onSelectTransition(existingTransitions[rowIndex].transitionId);
    },
    [existingTransitions, onSelectTransition]
  );

  const getRowClassName = useCallback(
    (rowIndex: number): string => {
      const isSelected = existingTransitions[rowIndex].transitionId === selectedTransitionId;
      return classNames(classes.clickableRow, isSelected && classes.selectedRow);
    },
    [existingTransitions, selectedTransitionId, classes]
  );

  return (
    <>
      <Stack spacing="sm">
        <Text>
          Select which transition to merge <strong>{newSourceNodeName}</strong> into
        </Text>
        <div
          className={classes.tableWrapper}
          {...getTestProps({component: 'MergeTransitionModal', element: 'transitionList'})}
        >
          <Table
            numRows={existingTransitions.length}
            calculateInView={false}
            fitContent={false}
            frozenHeader={false}
            getRowKey={(rowIndex) => existingTransitions[rowIndex].transitionId}
            includeNegativeMargins={false}
            isCompact={false}
            overflowVisible={true}
            tableLayout="fixed"
            onRowClick={handleRowClick}
            getRowClassName={getRowClassName}
          >
            <Column
              header={<Cell className={classes.radioColumn} />}
              renderCell={(rowIdx: number) => (
                <Cell className={classes.radioColumn}>
                  <Radio
                    aria-label={`Merge into transition from ${existingTransitions[rowIdx].fromNodes.map((n) => n.name).join(', ')} to ${existingTransitions[rowIdx].toNode.name}`}
                    checked={existingTransitions[rowIdx].transitionId === selectedTransitionId}
                    onChange={() => {
                      onSelectTransition(existingTransitions[rowIdx].transitionId);
                    }}
                    {...getTestProps({
                      component: 'MergeTransitionModal',
                      element: 'transitionOption',
                      key: existingTransitions[rowIdx].transitionId,
                    })}
                  />
                </Cell>
              )}
            />
            <Column
              header={<Cell className={classes.fromColumn}>From</Cell>}
              renderCell={(rowIdx: number) => {
                const option = existingTransitions[rowIdx];
                return (
                  <Cell className={classNames(classes.wrapCellText, classes.fromColumn)}>
                    <div className={classes.chipList}>
                      {option.fromNodes.map((node, idx) => (
                        <Chip
                          key={`${node.name}-${idx}`}
                          className={classes.displayOnlyChip}
                          size={ChipSize.MINI}
                          text={node.name}
                          icon={node.icon}
                        />
                      ))}
                    </div>
                  </Cell>
                );
              }}
            />
            <Column
              header={<Cell className={classes.toColumn}>To</Cell>}
              renderCell={(rowIdx: number) => {
                const option = existingTransitions[rowIdx];
                return (
                  <Cell className={classNames(classes.wrapCellText, classes.toColumn)}>
                    <div className={classes.chipList}>
                      <Chip
                        className={classes.displayOnlyChip}
                        size={ChipSize.MINI}
                        text={option.toNode.name}
                        icon={option.toNode.icon}
                      />
                    </div>
                  </Cell>
                );
              }}
            />
          </Table>
        </div>
      </Stack>
      <ModalFooter>
        <Group position="right">
          <Button
            variant="tertiary"
            onClick={onBack}
            {...getTestProps({component: 'MergeTransitionModal', element: 'backButton'})}
          >
            Back
          </Button>
          <Button
            onClick={onConfirm}
            disabled={selectedTransitionId == null}
            {...getTestProps({component: 'MergeTransitionModal', element: 'confirmMergeButton'})}
          >
            Merge
          </Button>
        </Group>
      </ModalFooter>
    </>
  );
}

export default function MergeTransitionModal(props: MergeTransitionModalProps): JSX.Element {
  const {onComplete, onClose, opened, existingTransitions, newSourceNodeName, targetNodeName} = props;

  const needsSelection = existingTransitions.length > 1;
  const [step, setStep] = useState<ModalStep>(ModalStep.CHOOSE_ACTION);
  const [selectedTransitionId, setSelectedTransitionId] = useState<string | null>(null);

  useEffect(() => {
    if (opened) {
      setStep(ModalStep.CHOOSE_ACTION);
      setSelectedTransitionId(existingTransitions.length === 1 ? existingTransitions[0].transitionId : null);
    }
  }, [opened, existingTransitions]);

  const handleClose = (): void => {
    onClose();
  };

  const handleMerge = (): void => {
    if (needsSelection && step === ModalStep.CHOOSE_ACTION) {
      setStep(ModalStep.SELECT_TRANSITION);
      return;
    }
    if (selectedTransitionId != null) {
      onComplete({shouldMerge: true, selectedTransitionId});
    }
  };

  const title = step === ModalStep.CHOOSE_ACTION ? 'Merge with an existing transition?' : 'Merge transitions';

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={title}
      size="lg"
      zIndex={ZINDEX_FLOATING_UI}
      closeButtonProps={{'aria-label': 'Close'}}
    >
      {step === ModalStep.CHOOSE_ACTION && (
        <ChooseActionStep
          targetNodeName={targetNodeName}
          newSourceNodeName={newSourceNodeName}
          existingTransitionCount={existingTransitions.length}
          onKeepSeparate={() => {
            onComplete({shouldMerge: false});
          }}
          onMerge={handleMerge}
        />
      )}
      {step === ModalStep.SELECT_TRANSITION && (
        <SelectTransitionStep
          newSourceNodeName={newSourceNodeName}
          existingTransitions={existingTransitions}
          selectedTransitionId={selectedTransitionId}
          onSelectTransition={setSelectedTransitionId}
          onBack={() => {
            setStep(ModalStep.CHOOSE_ACTION);
          }}
          onConfirm={handleMerge}
        />
      )}
    </Modal>
  );
}
